import React, { type ReactNode } from "react";
import IconComponent from "@CubeComponents/icons";
import Text from "@CubeComponents/html/Text/Text";
import { useTranslation } from "@i18n";
import type { TranslationKey } from "@i18n";

interface GridPageHeaderLeftProps {
  /** Icon name passed to IconComponent. Omit for settings/portal grids that don't use icons. */
  icon?: string;
  /** Page title — a TranslationKey string (auto-wrapped in t()) or a ReactNode for custom content */
  title: TranslationKey | ReactNode;
  /** Optional description — a TranslationKey string (auto-wrapped in t()) or a ReactNode for custom content */
  description?: TranslationKey | ReactNode;
}

interface GridPageHeaderProps {
  /** Icon name passed to IconComponent. Omit for settings/portal grids. */
  icon?: string;
  /** Page title — a TranslationKey string (auto-wrapped in t()) or a ReactNode for custom content */
  title: TranslationKey | ReactNode;
  /** Optional description — a TranslationKey string (auto-wrapped in t()) or a ReactNode for custom content */
  description?: TranslationKey | ReactNode;
  /** Custom rendered template instead of the default header. Use GridPageHeaderLeft inside for the standard left-side content. */
  customHeader?: ReactNode;
}

interface GridPageLayoutProps extends Partial<GridPageHeaderProps> {
  /** Whether to show the title/header section. Defaults to true. */
  showHeader?: boolean;
  /** Main grid content (SlickGrid1 + optional sidebar) */
  children: ReactNode;
  /** Additional className on the outermost gray wrapper */
  outerClassName?: string;
  /** Additional className on the inner white card */
  className?: string;
  /** Additional className on the grid container div */
  gridContainerClassName?: string;
}

/**
 * Reusable left-side header block: icon (optional) + title + description.
 * Export this so consumers can compose custom headers while keeping the standard left side.
 *
 * With icon:    [icon] Title        (module grids — Issues, Files, Reviews, etc.)
 *               Description
 *
 * Without icon: Title               (settings/portal grids)
 *               Description
 */
function GridPageHeaderLeft({ icon, title, description }: GridPageHeaderLeftProps) {
  const { t } = useTranslation();
  const isStringTitle = typeof title === "string";
  const isStringDesc = typeof description === "string";

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-start gap-x-4">
        {icon && <IconComponent name={icon} width="1.5rem" height="1.5rem" />}
        {isStringTitle ? (
          <Text className="text-2xl font-semibold">{t(title as TranslationKey)}</Text>
        ) : (
          <div className="text-2xl font-semibold">{title}</div>
        )}
      </div>
      {description &&
        (isStringDesc ? (
          <Text className="text-sm">{t(description as TranslationKey)}</Text>
        ) : (
          <div className="text-sm">{description}</div>
        ))}
    </div>
  );
}

function GridPageHeader({ icon, title, description, customHeader }: GridPageHeaderProps) {
  if (customHeader) {
    return <>{customHeader}</>;
  }

  return (
    <div className="flex flex-col pb-4">
      <GridPageHeaderLeft icon={icon} title={title} description={description} />
    </div>
  );
}

/**
 * Standardised grid page layout used across the platform.
 *
 * Structure:
 *   outer gray shell (bg-[#F0F5F9] rounded-lg)
 *     └─ inner white card (border rounded-lg p-4 bg-white)
 *          ├─ header (icon + title + description, or customHeader)
 *          └─ grid container (children)
 */
function GridPageLayout({
  showHeader = true,
  icon,
  title,
  description,
  customHeader,
  children,
  outerClassName = "",
  className = "",
  gridContainerClassName = "",
}: GridPageLayoutProps) {
  return (
    <div className={`w-full h-full bg-[#F0F5F9] rounded-lg overflow-hidden ${outerClassName}`.trim()}>
      <div className={`flex flex-col h-full w-full overflow-auto border rounded-lg p-4 bg-white ${className}`.trim()}>
        {showHeader && (customHeader || title) && (
          <GridPageHeader icon={icon} title={title ?? ""} description={description} customHeader={customHeader} />
        )}
        <div className={`flex w-full overflow-auto h-full ${gridContainerClassName}`.trim()}>
          <div className="h-full flex gap-6 w-full">{children}</div>
        </div>
      </div>
    </div>
  );
}

export { GridPageLayout, GridPageHeader, GridPageHeaderLeft };
export type { GridPageLayoutProps, GridPageHeaderProps, GridPageHeaderLeftProps };
export default GridPageLayout;
